import { useMemo, useState } from 'preact/hooks';
import type { Level } from '../../lib/levels.ts';
import { subjectProgress } from '../../lib/progression.ts';
import { live, useStore } from '../../store/store.ts';

interface Props {
  subject: string;
  level: Level;
}

/**
 * What is left to cover in one subject's référentiel.
 *
 * A percentage on its own only says how far there is to go; this says where.
 * It reads the lessons from the store rather than taking a snapshot, so it
 * stays right if a lesson is edited while it is open.
 */
export function ReferentielProgressModal({ subject, level }: Props) {
  const lessons = live(useStore((s) => s.lessons));
  const [onlyTodo, setOnlyTodo] = useState(false);

  const progress = useMemo(() => {
    const covered = new Set<string>();
    for (const lesson of lessons) for (const id of lesson.competenceIds) covered.add(id);
    return subjectProgress(subject, level, covered);
  }, [lessons, subject, level]);

  const visible = onlyTodo ? progress.points.filter((p) => !p.done) : progress.points;

  return (
    <div class="picker">
      <div class="pickerTools">
        <span class="small muted pickerSummary">
          {progress.donePoints} / {progress.totalPoints} contenus d’apprentissage ·{' '}
          {progress.coveredAttendus} / {progress.totalAttendus} attendus
        </span>
        <label class="pickerFilter">
          <input
            type="checkbox"
            checked={onlyTodo}
            onChange={(e) => setOnlyTodo(e.currentTarget.checked)}
          />
          <span>À couvrir ({progress.totalPoints - progress.donePoints})</span>
        </label>
      </div>

      <div class="progress">
        <span style={{ width: `${progress.percent}%` }} />
      </div>

      <div class="pickerList">
        {visible.length === 0 && (
          <p class="empty">
            {progress.totalPoints === 0
              ? 'Pas de référentiel du tronc commun pour cette discipline.'
              : 'Tout le référentiel a été couvert cette année.'}
          </p>
        )}

        {visible.map((point, index) => {
          const previous = visible[index - 1];
          return (
            <div key={`${point.section}|${point.parent}|${index}`}>
              {point.section !== previous?.section && (
                <h3 class="pickerSection">{point.section || 'Sans section'}</h3>
              )}
              <div class={point.done ? 'progressPoint done' : 'progressPoint'}>
                <span class="progressPointMark" aria-hidden="true">
                  {point.done ? '✓' : '·'}
                </span>
                <span class="pickerText">
                  {point.parent || 'Sans intitulé'}
                  <span class="pickerKind">
                    {point.coveredAttendus} / {point.attendus.length} attendus
                  </span>
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
